import { Heading, Icon, Tag, Text, TextButton, Upsell } from '@zillow/constellation-10';
import { IconHomesFilled } from '@zillow/constellation-icons';
import { useEffect, useState } from 'react';
import { UpsellTrackingIdType, UpsellType as UpsellTypeApi } from '@zg-rentals/ts-rins-client';
import {
  useUpsellWebApiHandler_clickUpsell,
  useUpsellWebApiHandler_viewUpsell,
} from '@zg-rentals/ts-rins-client-react-query';

import { UpsellDismissedStorageKey, UpsellEntryPointType } from '../../types';
import { hasUpsellBeenDismissed, useCreateUpsell } from '../../hooks/hooks';
import { CLICK_LEASE_UPSELL, VIEW_LEASE_UPSELL } from '../../utils/ClickstreamEvents';
import type { TrackClickstreamEventFn } from '../../utils/ClickstreamEvents';

type LeaseDashboardUpsellProps = {
  leaseId: string;
  onCreateUpsell?: (shouldDisplayUpsell: boolean) => void;
  onCloseUpsell?: () => void;
  trackClickstreamEvent: TrackClickstreamEventFn;
};

const TRACKING_ID_TYPE = UpsellTrackingIdType.LEASE_ID;
const UPSELL_TYPE = UpsellTypeApi.LEASES_PAGE;
const UPSELL_ENTRY_POINT_TYPE = UpsellEntryPointType.LEASES_DASHBOARD;

const LeaseDashboardUpsell = ({
  leaseId,
  onCloseUpsell,
  onCreateUpsell,
  trackClickstreamEvent,
}: LeaseDashboardUpsellProps) => {
  const [isUpsellOpen, setIsUpsellOpen] = useState(true);
  const [upsellInfo, setUpsellInfo] = useState({
    shouldDisplayUpsell: false,
    isInsuranceRequiredForLease: false,
    isInsuranceRequiredForListing: false,
    monthlyPriceEstimate: '',
    redirectUrl: '',
    upsellId: '',
    zip: '',
  });

  const { mutate: viewUpsellMutation } = useUpsellWebApiHandler_viewUpsell();
  const { mutate: clickUpsellMutation } = useUpsellWebApiHandler_clickUpsell();

  const { createUpsell: createUpsellMutation, isLoading } = useCreateUpsell({
    onSuccess: (data) => {
      const { id: upsellId, shouldDisplayUpsell, url: redirectUrl } = data.upsell;

      if (shouldDisplayUpsell) {
        setUpsellInfo({
          upsellId,
          redirectUrl,
          ...data.upsell,
        });

        onUpsellView(upsellId);
      }

      onCreateUpsell?.(shouldDisplayUpsell);
    },
    onError: () => {
      onCreateUpsell?.(false);
    },
  });

  useEffect(() => {
    if (!hasUpsellBeenDismissed(UPSELL_TYPE, TRACKING_ID_TYPE, leaseId)) {
      createUpsellMutation(UPSELL_TYPE, TRACKING_ID_TYPE, leaseId);
    } else {
      onCreateUpsell?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId]);

  const onUpsellView = (upsellId: string) => {
    trackClickstreamEvent(VIEW_LEASE_UPSELL(UPSELL_ENTRY_POINT_TYPE));
    viewUpsellMutation({ postData: { upsellId } });
  };

  const onUpsellClick = () => {
    window.open(upsellInfo.redirectUrl, '_blank');
    trackClickstreamEvent(CLICK_LEASE_UPSELL(UPSELL_ENTRY_POINT_TYPE));
    clickUpsellMutation({
      postData: {
        upsellId: upsellInfo.upsellId,
        sessionGuid: '',
      },
    });
  };

  const onClose = () => {
    localStorage.setItem(`${UpsellDismissedStorageKey.LEASES}${leaseId}`, JSON.stringify(false));
    setIsUpsellOpen(false);
    onCloseUpsell?.();
  };

  const isInsuranceRequired = upsellInfo.isInsuranceRequiredForLease || upsellInfo.isInsuranceRequiredForListing;
  const insuranceRequiredText = upsellInfo.isInsuranceRequiredForLease ? 'Required in lease' : 'Required by landlord';

  if (isLoading || !upsellInfo.shouldDisplayUpsell || !isUpsellOpen) {
    return null;
  }

  return (
    <Upsell
      data-testid="LeaseDashboardUpsell"
      header={
        <>
          {isInsuranceRequired && (
            <Tag data-testid="renters-insurance-required-tag" tone="gray" css={{ marginBottom: 'tighter' }}>
              {insuranceRequiredText}
            </Tag>
          )}
          <Heading level={5}>Get renters insurance fast</Heading>
        </>
      }
      body={
        <Text>
          Popular plans near {upsellInfo.zip} start at {upsellInfo.monthlyPriceEstimate}/month
        </Text>
      }
      media={<Icon render={<IconHomesFilled />} />}
      action={
        <TextButton data-testid="LeaseDashboardUpsell-GetStartedBtn" onClick={onUpsellClick}>
          Get started
        </TextButton>
      }
      elevated={false}
      onOpenChange={(open) => !open && onClose()}
    />
  );
};

export default LeaseDashboardUpsell;
